import { EMPTY_DATA_VIEW_STATE, type DataViewState, type DataViewSurface } from "./viewDefinition";
import { normalizeDataViewGroupBy, normalizeDataViewLayout } from "./viewUrlState";

const STORAGE_PREFIX = "forge:data-views:";

export interface LegacySavedDataView {
  readonly id: string;
  readonly surface: DataViewSurface;
  readonly name: string;
  readonly state: DataViewState;
  readonly isDefault: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface LegacySavedDataViewStoreSnapshot {
  readonly version: 1;
  readonly views: readonly LegacySavedDataView[];
}

export function legacySavedDataViewsStorageKey(surface: DataViewSurface): string {
  return `${STORAGE_PREFIX}${surface}:v1`;
}

function normalizeState(value: unknown): DataViewState {
  const input = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  return {
    layout: normalizeDataViewLayout(input["layout"]),
    search: readText(input, "search"),
    statusFilter: readText(input, "statusFilter"),
    workspaceFilter: readText(input, "workspaceFilter"),
    campaignFilter: readText(input, "campaignFilter"),
    ownerFilter: readText(input, "ownerFilter"),
    groupBy: normalizeDataViewGroupBy(input["groupBy"]),
    milestoneFilter: readText(input, "milestoneFilter"),
    taskGroupBy: input["taskGroupBy"] === "milestone" ? "milestone" : "status",
  };
}

function readText(input: Record<string, unknown>, key: string): string {
  return typeof input[key] === "string" ? input[key] : "";
}

function readRequiredText(input: Record<string, unknown>, key: string): string | null {
  const value = readText(input, key).trim();
  return value.length > 0 ? value : null;
}

function readDate(input: Record<string, unknown>, key: string): string {
  return typeof input[key] === "string" ? input[key] : new Date(0).toISOString();
}

function normalizeView(value: unknown, surface: DataViewSurface): LegacySavedDataView | null {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  const id = readRequiredText(input, "id");
  const name = readRequiredText(input, "name");
  if (!id || !name) return null;
  return {
    id,
    surface,
    name,
    state: normalizeState(input["state"] ?? EMPTY_DATA_VIEW_STATE),
    isDefault: input["isDefault"] === true,
    createdAt: readDate(input, "createdAt"),
    updatedAt: readDate(input, "updatedAt"),
  };
}

/** Read-only compatibility bridge for the pre-CMMD local saved-view store. */
export function readLegacySavedDataViews(surface: DataViewSurface): readonly LegacySavedDataView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(legacySavedDataViewsStorageKey(surface));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<LegacySavedDataViewStoreSnapshot>;
    const views = Array.isArray(parsed.views) ? parsed.views : [];
    return views.flatMap((view) => {
      const normalized = normalizeView(view, surface);
      return normalized ? [normalized] : [];
    });
  } catch {
    return [];
  }
}

/**
 * Clear legacy data only after CMMD confirms every idempotent import. A failed
 * or partial migration must leave the source intact for the next retry.
 */
export function clearLegacySavedDataViews(surface: DataViewSurface): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(legacySavedDataViewsStorageKey(surface));
  } catch {
    // Private-mode storage failures must not break the canonical surface.
  }
}
